/**
 * AutoDay domänmodell.
 *
 * Fyra saker hålls medvetet isär:
 *  - SourceItem: importerad originalhändelse (oföränderlig per importversion)
 *  - Overlay: användarens lokala lager ovanpå en källpost (anteckningar, klar-status, granskning)
 *  - LocalEvent: egna aktiviteter (privat/arbete), inklusive länkade arbetsaktiviteter
 *  - Occurrence / AlarmPoint: beräknade förekomster och larmpunkter (sparas inte som sanning)
 */

/** Kalenderdatum utan tid, YYYY-MM-DD. */
export type ISODate = string;
/** Absolut ögonblick i UTC, t.ex. 2026-10-11T07:00:00Z. */
export type ISOInstant = string;
/** Lokal väggklocka utan zon, YYYY-MM-DDTHH:mm. Tolkas alltid tillsammans med en IANA-zon. */
export type PlainDateTimeStr = string;

export type Timing =
  | {
      kind: 'allDay';
      start: ISODate;
      /** Exklusivt slutdatum enligt RFC 5545 (DTEND;VALUE=DATE). En endagshändelse har end = start + 1. */
      endExclusive: ISODate;
    }
  | {
      kind: 'timed';
      start: PlainDateTimeStr;
      end: PlainDateTimeStr;
      /** IANA-zon som väggklockan avser. */
      timeZone: string;
      /**
       * Hur längden bevaras vid flytt över sommartidsgränser:
       *  - 'wall': samma väggklocktider (08–16 förblir 08–16)
       *  - 'elapsed': samma faktiska längd i minuter (ett nattpass på 8 h förblir 8 h)
       */
      durationPolicy: 'wall' | 'elapsed';
    };

export type AlarmAction = 'DISPLAY' | 'AUDIO' | 'EMAIL' | 'PROCEDURE' | 'UNKNOWN';

export type Alarm =
  | {
      id: string;
      kind: 'absolute';
      /** Samma ögonblick som källan angav – aldrig omskrivet till tidszonslös väggtid. */
      at: ISOInstant;
      action: AlarmAction;
      description?: string;
      origin: 'source' | 'user';
    }
  | {
      id: string;
      kind: 'relative';
      /** Negativt = före. -15 = 15 minuter före. */
      offsetMinutes: number;
      related: 'start' | 'end';
      action: AlarmAction;
      description?: string;
      origin: 'source' | 'user';
    };

/** ISO-veckodag: 1 = måndag … 7 = söndag. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface RecurrenceRule {
  freq: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  /** 1 = varje, 2 = varannan … */
  interval: number;
  /** Endast WEEKLY. Saknas = startdagens veckodag. */
  byWeekday?: Weekday[];
  /** Antal förekomster totalt (inklusive första). */
  count?: number;
  /** Sista tillåtna förekomstdatum (inklusive), lokalt datum. */
  until?: ISODate;
}

/** Förekomstnyckel: ursprungligt lokalt startdatum (YYYY-MM-DD) för förekomsten. */
export type OccurrenceKey = string;

export interface OccurrenceOverride {
  timing?: Timing;
  title?: string;
  location?: string;
  notes?: string;
  cancelled?: boolean;
}

export interface DoneState {
  at: ISOInstant;
  /** För importerade skyldigheter: användaren bekräftade att åtgärden kontrollerats. */
  confirmedChecked?: boolean;
  note?: string;
}

export type CategoryId = string;

export interface Category {
  id: CategoryId;
  name: string;
  icon: IconId;
  builtIn: boolean;
  /** Känslig kategori → kan döljas på låsskärm. */
  sensitive?: boolean;
}

export type IconId =
  | 'home'
  | 'briefcase'
  | 'hospital'
  | 'stethoscope'
  | 'building'
  | 'landmark'
  | 'receipt'
  | 'wallet'
  | 'dumbbell'
  | 'plane'
  | 'car'
  | 'calendar'
  | 'star'
  | 'book'
  | 'users'
  | 'utensils'
  | 'pill'
  | 'baby'
  | 'graduation'
  | 'music'
  | 'heart';

export type CalendarKind = 'private' | 'work' | 'import';

export interface CalendarDef {
  id: string;
  name: string;
  kind: CalendarKind;
  color: string;
  visible: boolean;
  /** För importkalendrar: logisk importkälla. */
  sourceId?: string;
}

export interface LocalEvent {
  id: string;
  calendarId: string;
  title: string;
  categoryId: CategoryId;
  icon: IconId;
  timing: Timing;
  location?: string;
  notes?: string;
  /** Endast http(s) – kontrolleras med sanitizeUrl. */
  url?: string;
  alarms: Alarm[];
  recurrence?: RecurrenceRule;
  exdates?: OccurrenceKey[];
  overrides?: Record<OccurrenceKey, OccurrenceOverride>;
  /** Klar-status för icke-återkommande aktivitet. */
  done?: DoneState | null;
  /** Klar-status per förekomst för återkommande aktivitet. */
  doneOccurrences?: Record<OccurrenceKey, DoneState>;
  /** UID vid ICS-export. Nytt för varje kopia. */
  exportUid: string;
  /** Länkad privat arbetsaktivitet inför en källdeadline. Flytt av denna påverkar aldrig källan. */
  linkedSourceKey?: string;
  copiedFromId?: string;
  priority?: 'normal' | 'high';
  revision: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
  /** Tombstone för synk. */
  deleted?: boolean;
}

export const KNOWN_TAGS = [
  '[MÅSTE]',
  '[FÖRBEREDELSE]',
  '[FRIVILLIG KONTROLL]',
  '[KONTROLLERA]',
  '[INGEN BETALNING]',
  '[TEST]',
] as const;
export type KnownTag = (typeof KNOWN_TAGS)[number];

export interface TagInfo {
  /** Känd tagg, eller null om okänd/saknas. Hittas aldrig på. */
  tag: KnownTag | null;
  /** Exakt prefix som stod i källan, även okänt, t.ex. "[ÖVRIGT]". null om inget hakparentesprefix. */
  rawPrefix: string | null;
}

export interface SourceData {
  /** Originaltext exakt som i källan (efter avkodning). */
  summary: string;
  tag: TagInfo;
  /** SUMMARY utan igenkänt prefix – för kompakt visning. Originalet bevaras i summary. */
  title: string;
  description: string;
  /** Säker http(s)-länk eller null. */
  url: string | null;
  /** Länk som avvisades (t.ex. javascript:) – visas som text, aldrig klickbar. */
  rejectedUrl?: string;
  location: string;
  classification: string | null;
  /** TRANSPARENT blockerar inte tid i krockvarningar. */
  transparent: boolean;
  timing: Timing;
  alarms: Alarm[];
  recurrence?: RecurrenceRule;
  exdates?: OccurrenceKey[];
  dtstamp: ISOInstant | null;
  sequence: number | null;
  /** Egenskaper som lästes men inte stöds – visas som varning, inget tappas tyst. */
  unsupported: string[];
}

export interface SourceItemVersion {
  importId: string;
  contentHash: string;
  data: SourceData;
}

export interface SourceItem {
  /** sourceId|uid|recurrenceId – identitet, aldrig titel/filnamn/dag. */
  key: string;
  sourceId: string;
  uid: string;
  recurrenceId: string | null;
  data: SourceData;
  contentHash: string;
  firstImportId: string;
  lastSeenImportId: string;
  /** 'missing' = saknades i senaste snapshot. Raderas/klarmarkeras aldrig automatiskt. */
  presence: 'present' | 'missing';
  /** Användaren valde att dölja posten efter att den saknats. Kan återställas. */
  archived?: boolean;
  history: SourceItemVersion[];
  revision: number;
}

export type ReviewReason = 'sourceChangedWhileDone' | 'missingInLatest' | 'sourceChanged';

export interface Overlay {
  key: string;
  notes?: string;
  done?: DoneState | null;
  review?: { reason: ReviewReason; since: ISOInstant; importId: string; previousStart?: ISODate } | null;
  /**
   * Användarens uttryckliga val för Viktigt: true = räkna posten som deadline, false = inte en deadline.
   * Saknas = källans standard (se ImportSource.treatAsDeadlines). Ändras aldrig av en import.
   */
  deadline?: boolean | null;
  categoryId?: CategoryId;
  icon?: IconId;
  /**
   * Användaren avfärdade förslaget om förberedelseplan för just detta datum (deadlinens början när
   * förslaget avfärdades). Gäller tills användaren visar förslaget igen eller källans datum ändras.
   * Äldre klienter bevarar fältet (alla överläggsändringar sprider befintliga fält).
   */
  prepDismissed?: { at: ISOInstant; deadlineStart: ISOInstant } | null;
  revision: number;
}

export interface ImportRecord {
  id: string;
  sourceId: string;
  at: ISOInstant;
  fileName: string;
  fileSha256: string;
  fileSize: number;
  prodid: string | null;
  calendarName: string | null;
  /** Senaste DTSTAMP i filen. */
  fileDtstamp: ISOInstant | null;
  firstDate: ISODate | null;
  lastDate: ISODate | null;
  counts: { events: number; alarms: number; created: number; changed: number; unchanged: number; missing: number };
  /** Tillstånd före importen för varje berörd källpost (null = fanns inte). Används för Ångra import. */
  undo: Record<string, SourceItem | null>;
  undone?: boolean;
}

export interface ImportSource {
  id: string;
  name: string;
  calendarId: string;
  imports: ImportRecord[];
  /**
   * Räknas källans poster som deadlines under Viktigt? Saknas = standard: ja för SkatteFix-exporten,
   * nej för andra kalenderfiler (ett importerat möte är inte en skyldighet bara för att det importerats).
   */
  treatAsDeadlines?: boolean;
}

export interface QuietHours {
  enabled: boolean;
  /** HH:mm lokal tid */
  start: string;
  end: string;
}

export interface Settings {
  timeZone: string;
  timeZoneConfirmed: boolean;
  /**
   * Historiskt fält, alltid 'sv-SE'. Används INTE för presentation: språket är ett val per enhet
   * (webbappens 'autoday.language'). Behålls så att äldre profiler, säkerhetskopior och synk läses oförändrat.
   */
  locale: 'sv-SE';
  weekStartsOn: 1;
  quietHours: QuietHours;
  /** Dölj hälsouppgifter och företagsdetaljer på låsskärmen. */
  hideSensitiveOnLockScreen: boolean;
  defaultAlarmsMinutes: number[];
  maxSnoozesPerPoint: number;
  /** Sammanfatta importerade heldagsdeadlines med samma larmögonblick. */
  summarizeImportedAllDay: boolean;
}

export interface DayTemplate {
  id: string;
  name: string;
  /** 'day' = en dags aktiviteter, 'week' = måndag–söndag. Händelser lagras relativt mallens första dag. */
  span: 'day' | 'week';
  items: Array<Omit<LocalEvent, 'id' | 'exportUid' | 'revision' | 'createdAt' | 'updatedAt' | 'done' | 'doneOccurrences'> & { dayOffset: number }>;
  createdAt: ISOInstant;
}

// ---------------------------------------------------------------------------
// Förberedelseplaner (se docs/PREPARATION-PLANS.md)
// ---------------------------------------------------------------------------

/** Ett steg i en förberedelsemall. Identiteten (id) ändras aldrig när titel, längd eller ordning ändras. */
export interface PrepStepDef {
  id: string;
  /** Användarens egen text – översätts aldrig. */
  title: string;
  /** 15–240 minuter faktisk tid. */
  durationMinutes: number;
  /** Önskat antal dagar före deadline (1–14), räknat som kalenderdagar eller vardagar. */
  daysBefore: number;
}

/** Mallens innehåll – samma form i mallen och i planens ögonblicksbild. */
export interface PrepTemplateContent {
  name: string;
  /** 1–5 steg i tidsordning. */
  steps: PrepStepDef[];
  /** Hur "dagar före" räknas: vardagar = måndag–fredag (inga påhittade helgdagsregler). */
  dayCount: 'weekdays' | 'calendar';
  /** Tillåten tidsperiod varje dag, lokal väggtid HH:mm (kvartar). */
  window: { start: string; end: string };
  /** Tillåtna veckodagar för passen (ISO 1 = måndag … 7 = söndag). */
  weekdays: Weekday[];
  /** Egen, skrivbar målkalender (aldrig en importkalender). */
  calendarId: string;
}

export interface PrepTemplate extends PrepTemplateContent {
  id: string;
  /** Ökar vid varje ändring. Ett utkast avser en viss revision; en ändrad mall kräver nytt förslag. */
  revision: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/** Opt-in: föreslå planer automatiskt för kommande deadlines från EN vald importkälla. */
export interface PrepRule {
  /** Deterministiskt per källa (prepRuleIdFor) – högst en regel per källa. */
  id: string;
  sourceId: string;
  templateId: string;
  status: 'active' | 'paused';
  /** Varför regeln är pausad. Återställda/överförda regler börjar pausade. */
  pausedReason?: 'user' | 'template-deleted' | 'restored' | 'transferred' | null;
  revision: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/** Deadlinen som en plan byggdes för (vid senaste godkännandet). */
export interface PrepPlanBasis {
  sourceKey: string;
  /** Deadlinens början som ögonblick. Heldag = dagens början i zonen nedan. */
  deadlineStart: ISOInstant;
  deadlineDate: ISODate;
  allDay: boolean;
  /** Källpostens innehållshash när planen godkändes. */
  sourceHash: string;
  /** Visningszonen planen räknades i. */
  timeZone: string;
}

export interface PrepPlanStep {
  /** Stegets identitet från mallens ögonblicksbild. */
  stepId: string;
  title: string;
  durationMinutes: number;
  /** Stegets aktuella kalenderpass (en vanlig LocalEvent). */
  eventId: string;
  /** Det planen senast lade in. Avvikande revision på passet = användarens eget val. */
  approved: { start: PlainDateTimeStr; end: PlainDateTimeStr; timeZone: string; eventRevision: number; at: ISOInstant };
  /** Antal pass steget haft (ökar när ett borttaget steg uttryckligen återställs) – ger nya, stabila ID:n. */
  instance: number;
}

/**
 * Godkänd förberedelseplan. Planen – inte kalenderpasset – bär relationen deadline → steg → pass,
 * så att kopior av ett pass aldrig blir samma steg och äldre klienter inte kan tappa relationen.
 */
export interface PrepPlan {
  /** Deterministiskt per källpost (prepPlanIdFor) – en plan per källdeadline. */
  id: string;
  sourceKey: string;
  /** 'ended' = avslutad/borttagen. Ligger kvar så att varken förslag eller synk återuppväcker planen. */
  status: 'active' | 'ended';
  /** Ökar när en ny plan ersätter en avslutad för samma deadline. */
  generation: number;
  origin: 'manual' | 'rule';
  ruleId?: string | null;
  templateId: string;
  /** Mallen som den såg ut vid godkännandet. Senare mallredigering påverkar inte planen. */
  snapshot: PrepTemplateContent & { templateRevision: number };
  basis: PrepPlanBasis;
  steps: PrepPlanStep[];
  /** Krockar användaren uttryckligen valt att behålla (fingeravtryck). */
  acknowledged?: string[];
  history: Array<{ at: ISOInstant; kind: 'approve' | 'replan' | 'restore' | 'end' | 'import' | 'acknowledge'; opId: string }>;
  endedAt?: ISOInstant | null;
  revision: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

export interface AppState {
  schemaVersion: number;
  settings: Settings;
  calendars: Record<string, CalendarDef>;
  categories: Record<CategoryId, Category>;
  events: Record<string, LocalEvent>;
  sourceItems: Record<string, SourceItem>;
  overlays: Record<string, Overlay>;
  sources: Record<string, ImportSource>;
  templates: Record<string, DayTemplate>;
  /** Förberedelsemallar. Saknas i äldre data – fylls i av migrate(). */
  prepTemplates: Record<string, PrepTemplate>;
  /** Källregler för automatiska förslag (opt-in, en per källa). */
  prepRules: Record<string, PrepRule>;
  /** Godkända (och avslutade) förberedelseplaner, en per källdeadline. */
  prepPlans: Record<string, PrepPlan>;
  /** Tillämpade operations-ID:n – skydd mot dubbelklick och upprepade nätverksförsök. */
  appliedOps: Record<string, ISOInstant>;
}

/**
 * Domänschemat. Förberedelseplanerna lade till tre valfria samlingar ADDITIVT (migrate() fyller i dem)
 * utan att höja versionen: en äldre klient efter en återgång ska kunna öppna samma data.
 */
export const SCHEMA_VERSION = 1;
